import { isEqualWith } from "lodash";
import { DateTime } from "luxon";
import Decimal from "decimal.js-light";
import CreateShipmentData, { CreateEditShipmentStop } from "./CreateEditShipmentData";
import { StopUnit, CmrConfig } from "../../../ducks/app/cargo-and-template-shared";

/**
 * Three-way merge of the cargo dialog's form with a newer version of the same
 * cargo that someone else saved while the dialog was open (DORA-441).
 *
 * - `base`: the form values the dialog last loaded (what both sides started
 *   from).
 * - `mine`: the form values now, with the user's unsaved edits.
 * - `theirs`: the newer version from the server, mapped to form values the same
 *   way `base` was.
 *
 * A field changed on one side only takes that side's value. A field changed on
 * both sides to different values is a conflict. Rows of the list fields
 * (stops, stop units, CMR configs) are matched by identity, never by position,
 * so a row added, removed or moved by one side does not shift the other side's
 * edits onto a different row.
 */

// The base is only ever compared, never written back: react-hook-form types
// what the form started from as partial, so it is read as untyped values.
type Untyped = Record<string, unknown>;

export type Conflict =
  | { kind: "field"; field: string }
  | { kind: "row-field"; list: string; rowKey: string; field: string }
  // One side edited a row the other side deleted.
  | { kind: "row-deleted"; list: string; rowKey: string }
  // Both sides changed the order of a list, differently.
  | { kind: "order"; list: string }
  // A merged row points at a stop that the merge no longer has.
  | { kind: "dangling-reference"; list: string; rowKey: string };

export type MergeResult =
  | {
      kind: "merged";
      values: CreateShipmentData;
      // What the form starts from now: their version, with the form-only
      // fields as the user's form has them - those aren't edits, and their
      // version (loaded from the server) doesn't have them.
      start: CreateShipmentData;
      // The fields whose merged value isn't the start's: what the user
      // changed.
      differFromStart: Extract<keyof CreateShipmentData, string>[];
    }
  | { kind: "conflict"; conflicts: Conflict[] };

// How a field merges:
// - "merge": a change on one side is taken, the same field changed on both
//   sides differently is a conflict.
// - "local": form-only state, never compared; the user's value always stays.
// - "list": rows matched by identity and merged row by row (see mergeList).
// Every field of the form's types has to be named here, so a new field can't
// be added without deciding how it merges.
type Kind = "merge" | "local";
type Kinds<O> = { [K in keyof O]-?: Kind };

const FORM_FIELDS: { [K in keyof CreateShipmentData]-?: Kind | "list" } = {
  clientId: "merge",
  invoiceRef: "merge",
  type: "merge",
  qualities: "merge",
  requirements: "merge",
  products: "merge",
  description: "merge",
  description2: "merge",
  cargoTagId: "merge",
  adr: "merge",
  adrText: "merge",
  hideForDrivers: "merge",
  invoiceStatus: "merge",
  customerContactId: "merge",
  clientContact: "merge",
  clientContactPhone: "merge",
  clientContactEmail: "merge",
  driverCanAddDropoffs: "merge",
  driverCanRegisterTime: "merge",
  driverCanMoveFromToUndispatched: "merge",
  ref: "merge",
  note: "merge",
  assignedTeamId: "merge",
  minTemperature: "merge",
  maxTemperature: "merge",
  containerNo: "merge",
  extraCo2Elements: "merge",
  // The server's: the merged form takes the latest one.
  revision: "merge",
  // Never changes after creation, and loading a cargo clears it when its
  // template has since been deleted (see loadCargo); the server's copy would
  // put the stale reference back.
  clientCargoTemplateId: "local",
  INVOICE_REF_LOCKED: "local",
  LOADING_REF_LOCKED: "local",
  pickupList: "list",
  dropoffList: "list",
  stopUnits: "list",
  cmrConfigs: "list",
};

const STOP_FIELDS: Kinds<CreateEditShipmentStop> = {
  id: "merge",
  provisoryId: "merge",
  loadmeters: "merge",
  height: "merge",
  width: "merge",
  length: "merge",
  weight: "merge",
  colli: "merge",
  cubicMeters: "merge",
  place: "merge",
  ref: "merge",
  tag: "merge",
  contact: "merge",
  contactPhone: "merge",
  fix: "merge",
  note: "merge",
  date: "merge",
  time: "merge",
  openingDate: "merge",
  openingTime: "merge",
  pieces: "merge",
  // Drag-and-drop key and "touched" marker, regenerated on every load.
  flipKey: "local",
  modified: "local",
  CMR_TAG_LOCKED: "local",
  HAS_SORTING_DATE_BEEN_CHANGED: "local",
};

const STOP_UNIT_FIELDS: Kinds<StopUnit> = {
  id: "merge",
  pickupId: "merge",
  dropoffId: "merge",
  weight: "merge",
  width: "merge",
  length: "merge",
  height: "merge",
  volume: "merge",
  colli: "merge",
  quantity: "merge",
  stopUnitTemplateId: "merge",
  description: "merge",
};

const CMR_CONFIG_FIELDS: Kinds<CmrConfig> = {
  id: "merge",
  pickupId: "merge",
  dropoffId: "merge",
};

const fieldsOfKind = (fields: Record<string, string>, kind: string) =>
  new Set(Object.keys(fields).filter((field) => fields[field] === kind));

const FORM_LOCAL = fieldsOfKind(FORM_FIELDS, "local");
const FORM_LISTS = fieldsOfKind(FORM_FIELDS, "list");

type RowIdentity = { id?: unknown; provisoryId?: unknown };

type ListSpec = {
  // The row's identity across versions; rows without one are skipped.
  key: (row: RowIdentity) => string | undefined;
  // Whether the order of the rows means something.
  ordered: boolean;
  // The row's fields that are never compared.
  local: ReadonlySet<string>;
};

const isUntyped = (value: unknown): value is Untyped =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// The rows of a list field of the base, which is untyped; anything that isn't
// a row (an absent list) has none.
const rowsOf = (value: unknown): Untyped[] =>
  Array.isArray(value) ? value.filter(isUntyped) : [];

const text = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : undefined;

const stopKey = (row: RowIdentity) => text(row.id) ?? text(row.provisoryId);
const idKey = (row: RowIdentity) => text(row.id);

const STOPS: ListSpec = {
  key: stopKey,
  ordered: true,
  local: fieldsOfKind(STOP_FIELDS, "local"),
};
const STOP_UNITS: ListSpec = {
  key: idKey,
  ordered: false,
  local: fieldsOfKind(STOP_UNIT_FIELDS, "local"),
};
const CMR_CONFIGS: ListSpec = {
  key: idKey,
  ordered: false,
  local: fieldsOfKind(CMR_CONFIG_FIELDS, "local"),
};

// Values that are the same but not structurally identical: luxon caches
// computed fields on the instance, and the same decimal can be stored
// differently.
export const sameValue = (a: unknown, b: unknown): boolean =>
  isEqualWith(a, b, (x, y) => {
    if (DateTime.isDateTime(x) && DateTime.isDateTime(y)) {
      return x.toMillis() === y.toMillis();
    }
    if (x instanceof Decimal && y instanceof Decimal) {
      return x.eq(y);
    }
    // Empty and absent are the same for a form field.
    if ((x === undefined || x === null) && (y === undefined || y === null)) {
      return true;
    }
    return undefined;
  });

type FieldMerge<V> = { ok: true; value: V } | { ok: false };

const mergeValue = <V>(base: unknown, mine: V, theirs: V): FieldMerge<V> => {
  const mineChanged = !sameValue(base, mine);
  const theirsChanged = !sameValue(base, theirs);
  if (mineChanged && theirsChanged && !sameValue(mine, theirs)) {
    return { ok: false };
  }
  return { ok: true, value: mineChanged ? mine : theirs };
};

// Merges the fields of one object (the form, or one row of a list), starting
// from theirs. Fields in `skip` are left as theirs, for the caller to merge.
// The form also carries fields its type doesn't name (the rest of the loaded
// cargo); those merge like any other.
const mergeFields = <O extends object>(
  local: ReadonlySet<string>,
  skip: ReadonlySet<string>,
  base: Untyped,
  mine: O,
  theirs: O,
  onConflict: (field: string) => void
): O => {
  const merged = { ...theirs };
  for (const field in mine) {
    if (skip.has(field)) {
      continue;
    }
    if (local.has(field)) {
      merged[field] = mine[field];
      continue;
    }
    const result = mergeValue(base[field], mine[field], theirs[field]);
    if (result.ok) {
      merged[field] = result.value;
    } else {
      onConflict(field);
    }
  }
  return merged;
};

const NOTHING: ReadonlySet<string> = new Set();

// Whether a row differs from its base, form-only fields aside.
const rowChanged = <R extends object>(
  local: ReadonlySet<string>,
  base: Untyped,
  other: R
) => {
  for (const field in other) {
    if (!local.has(field) && !sameValue(base[field], other[field])) {
      return true;
    }
  }
  return Object.keys(base).some(
    (field) =>
      !local.has(field) && !(field in other) && !sameValue(base[field], null)
  );
};

const byKey = <R extends RowIdentity>(rows: R[], spec: ListSpec) => {
  const map = new Map<string, R>();
  for (const row of rows) {
    const key = spec.key(row);
    if (key) {
      map.set(key, row);
    }
  }
  return map;
};

const keysOf = (rows: RowIdentity[], spec: ListSpec) =>
  rows.map(spec.key).filter((k): k is string => !!k);

// The order of the rows both lists share, as a list of keys.
const sharedOrder = (
  rows: string[],
  others: { has: (key: string) => boolean }
) =>
  rows.filter((k) => others.has(k));

const mergeList = <R extends object & RowIdentity>(
  list: string,
  spec: ListSpec,
  baseRows: Untyped[],
  mineRows: R[],
  theirRows: R[],
  conflicts: Conflict[]
): R[] => {
  const base = byKey(baseRows, spec);
  const mine = byKey(mineRows, spec);
  const theirs = byKey(theirRows, spec);

  // Every row the merge keeps, merged, by key.
  const kept = new Map<string, R>();
  for (const key of new Set([...base.keys(), ...mine.keys(), ...theirs.keys()])) {
    const b = base.get(key);
    const m = mine.get(key);
    const th = theirs.get(key);
    if (b) {
      if (m && th) {
        kept.set(
          key,
          mergeFields(spec.local, NOTHING, b, m, th, (field) =>
            conflicts.push({ kind: "row-field", list, rowKey: key, field })
          )
        );
      } else if (m && !th) {
        // They deleted it: fine unless the user edited it.
        if (rowChanged(spec.local, b, m)) {
          conflicts.push({ kind: "row-deleted", list, rowKey: key });
        }
      } else if (!m && th) {
        // The user deleted it: fine unless they edited it.
        if (rowChanged(spec.local, b, th)) {
          conflicts.push({ kind: "row-deleted", list, rowKey: key });
        }
      }
      // Deleted on both sides: gone.
    } else if (m) {
      kept.set(key, m); // Added by the user.
    } else if (th) {
      kept.set(key, th); // Added by them.
    }
  }

  const keptRows = (keys: string[]) =>
    keys.flatMap((k) => {
      const row = kept.get(k);
      return row ? [row] : [];
    });

  const mineOrder = keysOf(mineRows, spec);
  const theirOrder = keysOf(theirRows, spec);
  if (!spec.ordered) {
    // The user's rows in their order, then rows they added.
    return keptRows([...mineOrder, ...theirOrder.filter((k) => !mine.has(k))]);
  }

  // Did a side reorder the rows it shares with the base?
  const baseOrder = keysOf(baseRows, spec);
  const mineReordered = !sameValue(
    sharedOrder(baseOrder, new Set(mineOrder)),
    sharedOrder(mineOrder, base)
  );
  const theirsReordered = !sameValue(
    sharedOrder(baseOrder, new Set(theirOrder)),
    sharedOrder(theirOrder, base)
  );
  if (mineReordered && theirsReordered) {
    const keptKeys = new Set(kept.keys());
    if (
      !sameValue(
        sharedOrder(mineOrder, keptKeys),
        sharedOrder(theirOrder, keptKeys)
      )
    ) {
      conflicts.push({ kind: "order", list });
      return [];
    }
  }

  // Follow the order of the side that reordered (the user's when neither or
  // both did), and slot each row only the other side has right after the row
  // it follows on that side.
  const [leading, following] = theirsReordered && !mineReordered
    ? [theirOrder, mineOrder]
    : [mineOrder, theirOrder];
  const order = leading.filter((k) => kept.has(k));
  following.forEach((key, index) => {
    if (!kept.has(key) || order.includes(key)) {
      return;
    }
    const predecessor = following
      .slice(0, index)
      .reverse()
      .find((k) => order.includes(k));
    order.splice(predecessor ? order.indexOf(predecessor) + 1 : 0, 0, key);
  });
  return keptRows(order);
};

// Stop units and CMR configs point at stops by id (or provisoryId for a stop
// not saved yet). After the merge every such pointer must still find its stop.
const checkReferences = (values: CreateShipmentData, conflicts: Conflict[]) => {
  const stopKeys = new Set(
    [...values.pickupList, ...values.dropoffList]
      .map(stopKey)
      .filter(Boolean)
  );
  const lists: [string, { id: string; pickupId: string; dropoffId: string | null }[]][] = [
    ["stopUnits", values.stopUnits],
    ["cmrConfigs", values.cmrConfigs],
  ];
  for (const [list, rows] of lists) {
    for (const row of rows) {
      const pointers = [row.pickupId, row.dropoffId].filter(
        (p): p is string => typeof p === "string"
      );
      if (pointers.some((p) => !stopKeys.has(p))) {
        conflicts.push({ kind: "dangling-reference", list, rowKey: row.id });
      }
    }
  }
};

// An object of theirs with the given form-only fields taken from mine.
const withLocalFields = <O extends object>(
  local: ReadonlySet<string>,
  mine: O,
  theirs: O
): O => {
  const start = { ...theirs };
  for (const field in mine) {
    if (local.has(field)) {
      start[field] = mine[field];
    }
  }
  return start;
};

// Their rows, each with the form-only fields of the user's row it matches.
const rowsWithLocalFields = <R extends object & RowIdentity>(
  spec: ListSpec,
  mine: R[],
  theirs: R[]
): R[] => {
  const mineByKey = byKey(mine, spec);
  return theirs.map((row) => {
    const key = spec.key(row);
    const mineRow = key ? mineByKey.get(key) : undefined;
    return mineRow ? withLocalFields(spec.local, mineRow, row) : row;
  });
};

// Where the form starts from after a merge: see MergeResult.
const startFrom = (
  mine: CreateShipmentData,
  theirs: CreateShipmentData
): CreateShipmentData => ({
  ...withLocalFields(FORM_LOCAL, mine, theirs),
  pickupList: rowsWithLocalFields(STOPS, mine.pickupList, theirs.pickupList),
  dropoffList: rowsWithLocalFields(STOPS, mine.dropoffList, theirs.dropoffList),
  stopUnits: rowsWithLocalFields(STOP_UNITS, mine.stopUnits, theirs.stopUnits),
  cmrConfigs: rowsWithLocalFields(
    CMR_CONFIGS,
    mine.cmrConfigs,
    theirs.cmrConfigs
  ),
});

const fieldsDiffering = <O extends object>(values: O, theirs: O) => {
  const fields: Extract<keyof O, string>[] = [];
  for (const field in values) {
    if (!sameValue(values[field], theirs[field])) {
      fields.push(field);
    }
  }
  return fields;
};

export const mergeRemoteChanges = (input: {
  base: Untyped;
  mine: CreateShipmentData;
  theirs: CreateShipmentData;
}): MergeResult => {
  const { base, mine, theirs } = input;
  const conflicts: Conflict[] = [];
  const values = mergeFields(FORM_LOCAL, FORM_LISTS, base, mine, theirs, (field) =>
    conflicts.push({ kind: "field", field })
  );
  values.pickupList = mergeList(
    "pickupList",
    STOPS,
    rowsOf(base.pickupList),
    mine.pickupList,
    theirs.pickupList,
    conflicts
  );
  values.dropoffList = mergeList(
    "dropoffList",
    STOPS,
    rowsOf(base.dropoffList),
    mine.dropoffList,
    theirs.dropoffList,
    conflicts
  );
  values.stopUnits = mergeList(
    "stopUnits",
    STOP_UNITS,
    rowsOf(base.stopUnits),
    mine.stopUnits,
    theirs.stopUnits,
    conflicts
  );
  values.cmrConfigs = mergeList(
    "cmrConfigs",
    CMR_CONFIGS,
    rowsOf(base.cmrConfigs),
    mine.cmrConfigs,
    theirs.cmrConfigs,
    conflicts
  );

  if (conflicts.length === 0) {
    checkReferences(values, conflicts);
  }
  if (conflicts.length > 0) {
    return { kind: "conflict", conflicts };
  }
  const start = startFrom(mine, theirs);
  return {
    kind: "merged",
    values,
    start,
    differFromStart: fieldsDiffering(values, start),
  };
};
